<template>
  <div class="reserve-calendar">
    <!-- 日付選択 -->
    <div class="date-selector" ref="dateSection">
      <h3 class="section-title">予約日を選択</h3>
      <calendar-date-picker 
        v-model="selectedDate"
        :reservation-type="reservationType"
        @update:modelValue="onDateChange"
      />
    </div>

    <!-- 開始時間選択 -->
    <div class="start-time-selector" v-if="selectedDate && reservationType === 'レンタル'" ref="startTimeSection">
      <h3 class="section-title">開始時間を選択</h3>
      <div class="time-select-wrapper">
        <select v-model="selectedStartTime" @change="onStartTimeChange" class="time-select">
          <option value="">開始時間を選択してください</option>
          <option v-for="time in startTimeOptions" :key="time" :value="time">
            {{ time }}
          </option>
        </select>
      </div>
    </div>

    <!-- 終了時間選択 -->
    <div class="end-time-selector" v-if="selectedDate && selectedStartTime && reservationType === 'レンタル'" ref="endTimeSection">
      <h3 class="section-title">終了時間を選択</h3>
      
      <!-- ローディング表示 -->
      <div v-if="isLoading" class="loading">
        <i class="fas fa-spinner fa-spin"></i>
        <span>空き状況を確認中...</span>
      </div>
      
      <!-- エラー表示 -->
      <div v-else-if="error" class="error-message">
        <i class="fas fa-exclamation-circle"></i>
        {{ error }}
      </div>
      
      <!-- 時間選択 -->
      <div v-else>
        <div class="time-select-wrapper">
          <select v-model="selectedEndTime" @change="onEndTimeChange" class="time-select">
            <option value="">終了時間を選択してください</option>
            <option v-for="time in endTimeOptions" :key="time" :value="time">
              {{ time }}
            </option>
          </select>
        </div>
        <div v-if="selectedEndTime" class="duration-display">
          <i class="fas fa-clock"></i>
          利用時間: {{ calculateDurationDisplay() }}
        </div>
      </div>
    </div>

    <!-- 見学の場合の固定時間表示 -->
    <div class="duration-selector" v-if="selectedDate && reservationType === '見学'" ref="durationSection">
      <h3 class="section-title">利用時間</h3>
      <div class="duration-fixed">
        <p class="duration-info">
          <i class="fas fa-info-circle"></i>
          見学は1時間となります
        </p>
      </div>
    </div>

    <!-- 時間選択（見学専用） -->
    <div class="time-selector" v-if="selectedDate && selectedDuration && reservationType === '見学'" ref="timeSection">
      <h3 class="section-title">開始時間を選択</h3>
      
      <!-- 見学の場合の案内メッセージ -->
      <div v-if="reservationType === '見学'" class="inspection-notice">
        <p class="inspection-info">
          <i class="fas fa-info-circle"></i>
          見学は10:00-11:00のみとなります。それ以外の時間帯をご希望の場合はお問い合わせフォームからお問い合わせください。
        </p>
      </div>
      
      <!-- ローディング表示 -->
      <div v-if="isLoading" class="loading">
        <i class="fas fa-spinner fa-spin"></i>
        <span>予約可能時間を確認中...</span>
      </div>

      <!-- エラー表示 -->
      <div v-else-if="error" class="error-message">
        <i class="fas fa-exclamation-circle"></i>
        {{ error }}
      </div>

      <!-- 時間スロット表示 -->
      <div v-else-if="availableSlots.length > 0" class="time-slots">
        <button
          v-for="slot in availableSlots"
          :key="slot.start"
          class="time-slot"
          :class="{ 'selected': selectedTime === slot.display }"
          @click="selectTime(slot)"
        >
          {{ slot.display }}
        </button>
      </div>

      <!-- 予約可能時間なし -->
      <div v-else class="no-slots">
        <i class="fas fa-calendar-times"></i>
        <p>選択された日は予約可能な時間がありません。</p>
        <p>別の日をお選びください。</p>
      </div>
    </div>

    <!-- 選択内容の確認 -->
    <div class="selection-summary" v-if="selectedDate && ((reservationType === 'レンタル' && selectedStartTime && selectedEndTime) || (reservationType === '見学' && selectedTime))" ref="summarySection">
      <h3 class="section-title">選択内容</h3>
      <div class="summary-content">
        <div class="summary-item">
          <span class="label">予約日：</span>
          <span class="value">{{ formatDate(selectedDate) }}</span>
        </div>
        <div class="summary-item" v-if="reservationType === 'レンタル'">
          <span class="label">開始時間：</span>
          <span class="value">{{ selectedStartTime }}</span>
        </div>
        <div class="summary-item" v-if="reservationType === 'レンタル'">
          <span class="label">終了時間：</span>
          <span class="value">{{ selectedEndTime }}</span>
        </div>
        <div class="summary-item" v-if="reservationType === 'レンタル'">
          <span class="label">利用時間：</span>
          <span class="value">{{ calculateDurationDisplay() }}</span>
        </div>
        <div class="summary-item" v-if="reservationType === '見学'">
          <span class="label">開始時間：</span>
          <span class="value">{{ selectedTime }}</span>
        </div>
        <div class="summary-item" v-if="reservationType === '見学'">
          <span class="label">利用時間：</span>
          <span class="value">{{ selectedDuration }}分</span>
        </div>
        <div class="summary-item" v-if="reservationType === '見学'">
          <span class="label">終了時間：</span>
          <span class="value">{{ endTime }}</span>
        </div>
        <div class="summary-item" v-if="reservationType === 'レンタル'">
          <span class="label">ご利用料金：</span>
          <span class="value price">¥{{ priceDisplay }}（税込）</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CalendarDatePicker from './CalendarDatePicker.vue';

export default {
  name: 'ReserveCalendar',
  components: {
    CalendarDatePicker
  },
  props: {
    modelValue: {
      type: Object,
      default: () => ({
        date: '',
        time: '',
        duration: 120
      })
    },
    reservationType: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectedDate: '',
      selectedTime: '',
      selectedDuration: 120,
      selectedStartTime: '',
      selectedEndTime: '',
      availableSlots: [],
      reservedTimes: [],  // その日の予約済み時間帯
      isLoading: false,
      error: null
    };
  },
  computed: {
    // 開始時間の選択肢（予約済み時間を考慮）
    startTimeOptions() {
      const times = [];
      const now = new Date();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // 選択された日付が今日かチェック
      const selectedDateObj = new Date(this.selectedDate);
      const isToday = selectedDateObj.toDateString() === today.toDateString();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      
      for (let hour = 9; hour <= 20; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          // 今日の場合、現在時刻より前は選択不可
          if (isToday && (hour < currentHour || (hour === currentHour && minute <= currentMinute))) {
            continue;
          }

          const time = `${hour}:${String(minute).padStart(2, '0')}`;

          // この時間から開始可能かチェック（最低15分は利用可能である必要がある）
          if (this.canStartAt(time)) {
            times.push(time);
          }
        }
      }

      // 21:00は最終開始時間として特別扱い（最低2時間で23:00終了が成立）
      if (!isToday && this.canStartAt('21:00')) {
        times.push('21:00');
      }
      
      return times;
    },
    // 終了時間の選択肢（予約済み時間を考慮）
    endTimeOptions() {
      if (!this.selectedStartTime) return [];
      
      const times = [];
      const [startHour, startMinute] = this.selectedStartTime.split(':').map(Number);
      const startTotalMinutes = startHour * 60 + startMinute;
      
      // 次の予約までの最大終了時間を取得
      const maxEndTime = this.getMaxEndTime(this.selectedStartTime);
      const [maxHour, maxMinute] = maxEndTime.split(':').map(Number);
      const maxTotalMinutes = maxHour * 60 + maxMinute;
      
      // レンタルの最低利用時間は2時間（120分）
      const minEndMinutes = startTotalMinutes + 120;
      
      // 最低2時間後から最大終了時間まで、15分刻みで選択肢を生成
      for (let totalMinutes = minEndMinutes; totalMinutes <= maxTotalMinutes; totalMinutes += 15) {
        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;
        if (hour <= 23) {
          const time = `${hour}:${String(minute).padStart(2, '0')}`;
          times.push(time);
        }
      }
      return times;
    },
    endTime() {
      if (this.reservationType === 'レンタル') {
        return this.selectedEndTime;
      }
      
      // 見学の場合
      if (!this.selectedTime || !this.selectedDuration) return '';
      
      const [hours, minutes] = this.selectedTime.replace('〜', '').split(':').map(Number);
      const endDate = new Date();
      endDate.setHours(hours, minutes, 0, 0);
      endDate.setMinutes(endDate.getMinutes() + this.selectedDuration);
      
      return `${endDate.getHours()}:${String(endDate.getMinutes()).padStart(2, '0')}`;
    },
    selectedDurationComputed() {
      if (this.reservationType === 'レンタル' && this.selectedStartTime && this.selectedEndTime) {
        const [startHour, startMinute] = this.selectedStartTime.split(':').map(Number);
        const [endHour, endMinute] = this.selectedEndTime.split(':').map(Number);
        return (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
      }
      return this.selectedDuration;
    },
    totalPrice() {
      // 1時間1,440円の計算
      const duration = this.reservationType === 'レンタル' && this.selectedStartTime && this.selectedEndTime
        ? this.selectedDurationComputed
        : this.selectedDuration;
      return (duration / 60) * 1440;
    },
    priceDisplay() {
      return this.totalPrice.toLocaleString();
    }
  },
  methods: {
    // その日の予約済み時間を取得
    async fetchReservedTimes() {
      if (!this.selectedDate) return;
      
      this.isLoading = true;
      this.error = null;
      
      try {
        const endpoint = process.env.NODE_ENV === 'production'
          ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/getReservedTimes'
          : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/getReservedTimes';
        
        const response = await axios.get(endpoint, {
          params: {
            date: this.selectedDate
          }
        });
        
        if (response.data.success) {
          this.reservedTimes = response.data.reservedTimes || [];
        } else {
          this.error = '予約状況の取得に失敗しました';
        }
      } catch (error) {
        console.error('Reserved times fetch error:', error);
        this.error = '予約状況の取得に失敗しました';
      } finally {
        this.isLoading = false;
      }
    },
    
    // 指定時間から開始可能かチェック
    canStartAt(startTime) {
      if (this.reservedTimes.length === 0) return true;
      
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMinute;
      
      // レンタルの最低利用時間は2時間（120分）
      const minEndMinutes = startMinutes + 120;
      
      // 営業終了時間（23:00 = 1380分）を超える場合は選択不可
      if (minEndMinutes > 23 * 60) {
        return false;
      }
      
      for (const reserved of this.reservedTimes) {
        const reservedStartMinutes = this.timeToMinutes(reserved.start);
        const reservedEndMinutes = this.timeToMinutes(reserved.end);
        
        // 開始時間が予約と重複している場合
        if (startMinutes >= reservedStartMinutes && startMinutes < reservedEndMinutes) {
          return false;
        }
        
        // 最低利用時間（2時間）内に予約がある場合
        if (reservedStartMinutes > startMinutes && reservedStartMinutes < minEndMinutes) {
          return false;
        }
      }
      
      return true;
    },
    
    // 指定開始時間からの最大終了時間を取得
    getMaxEndTime(startTime) {
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMinute;
      
      let maxEndMinutes = 23 * 60; // デフォルトは23:00
      
      // 次の予約を探す
      for (const reserved of this.reservedTimes) {
        const reservedStartMinutes = this.timeToMinutes(reserved.start);
        
        // 開始時間より後の予約で、最も早いものを探す
        if (reservedStartMinutes > startMinutes && reservedStartMinutes < maxEndMinutes) {
          maxEndMinutes = reservedStartMinutes;
        }
      }
      
      const hour = Math.floor(maxEndMinutes / 60);
      const minute = maxEndMinutes % 60;
      return `${hour}:${String(minute).padStart(2, '0')}`;
    },
    
    // 時間文字列を分に変換
    timeToMinutes(timeStr) {
      // ISO形式の場合
      if (timeStr.includes('T')) {
        const date = new Date(timeStr);
        const jstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000);
        return jstDate.getUTCHours() * 60 + jstDate.getUTCMinutes();
      }
      // HH:MM形式の場合
      const [hour, minute] = timeStr.split(':').map(Number);
      return hour * 60 + minute;
    },
    
    onStartTimeChange() {
      // 開始時間が変更されたら終了時間をリセット
      this.selectedEndTime = '';
      // 終了時間セクションへスクロール
      this.$nextTick(() => {
        this.scrollToSection('endTimeSection');
      });
    },
    async onEndTimeChange() {
      // 終了時間が選択されたら空き状況をチェック
      if (!this.selectedDate || !this.selectedStartTime || !this.selectedEndTime) return;
      
      this.isLoading = true;
      this.error = null;
      
      try {
        const endpoint = process.env.NODE_ENV === 'production'
          ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/checkTimeAvailability'
          : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/checkTimeAvailability';
        
        const response = await axios.post(endpoint, {
          date: this.selectedDate,
          startTime: this.selectedStartTime,
          endTime: this.selectedEndTime
        });
        
        if (response.data.success) {
          if (response.data.available) {
            // 予約可能な場合
            this.emitUpdate();
            // 選択内容セクションへスクロール
            this.$nextTick(() => {
              this.scrollToSection('summarySection');
            });
          } else {
            // 予約不可の場合
            this.error = response.data.message || '選択された時間帯は既に予約があります';
            this.selectedEndTime = '';
            alert('選択された時間帯は既に予約があります。別の時間をお選びください。');
          }
        } else {
          this.error = '空き状況の確認に失敗しました';
          this.selectedEndTime = '';
        }
      } catch (error) {
        console.error('Time availability check error:', error);
        this.error = '空き状況の確認に失敗しました';
        this.selectedEndTime = '';
      } finally {
        this.isLoading = false;
      }
    },
    calculateDurationDisplay() {
      if (!this.selectedStartTime || !this.selectedEndTime) return '';
      
      const [startHour, startMinute] = this.selectedStartTime.split(':').map(Number);
      const [endHour, endMinute] = this.selectedEndTime.split(':').map(Number);
      
      const startTotalMinutes = startHour * 60 + startMinute;
      const endTotalMinutes = endHour * 60 + endMinute;
      
      const durationMinutes = endTotalMinutes - startTotalMinutes;
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      
      if (hours > 0 && minutes > 0) {
        return `${hours}時間${minutes}分`;
      } else if (hours > 0) {
        return `${hours}時間`;
      } else {
        return `${minutes}分`;
      }
    },
    async fetchAvailableTimes() {
      if (!this.selectedDate || !this.selectedDuration) return;

      this.isLoading = true;
      this.error = null;
      this.availableSlots = [];
      this.selectedTime = '';

      // 見学の場合は10:00のみに制限
      if (this.reservationType === '見学') {
        try {
          const endpoint = process.env.NODE_ENV === 'production'
            ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/getAvailableTimes'
            : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/getAvailableTimes';

          const response = await axios.get(endpoint, {
            params: {
              date: this.selectedDate,
              duration: 60
            }
          });

          if (response.data.success) {
            // 10:00の枠のみをフィルタリング
            this.availableSlots = response.data.availableSlots.filter(slot => 
              slot.start === '10:00' || slot.display === '10:00〜'
            );
          } else {
            this.error = '予約可能時間の取得に失敗しました。';
          }
        } catch (error) {
          console.error('Available times fetch error:', error);
          this.error = '予約可能時間の取得に失敗しました。';
        } finally {
          this.isLoading = false;
        }
        return;
      }

      // レンタルの場合は通常通り
      try {
        const endpoint = process.env.NODE_ENV === 'production'
          ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/getAvailableTimes'
          : 'http://127.0.0.1:5001/kaonr-front/asia-northeast1/getAvailableTimes';

        const response = await axios.get(endpoint, {
          params: {
            date: this.selectedDate,
            duration: this.selectedDuration
          }
        });

        if (response.data.success) {
          this.availableSlots = response.data.availableSlots;
        } else {
          this.error = '予約可能時間の取得に失敗しました。';
        }
      } catch (error) {
        console.error('Error fetching available times:', error);
        this.error = 'サーバーとの通信に失敗しました。しばらくしてから再度お試しください。';
      } finally {
        this.isLoading = false;
      }
    },
    async onDateChange() {
      // 日付が変更されたら時間選択をリセット
      this.selectedTime = '';
      this.selectedStartTime = '';
      this.selectedEndTime = '';
      
      if (this.reservationType === 'レンタル') {
        // レンタルの場合は予約済み時間を取得
        await this.fetchReservedTimes();
        // 開始時間セクションへスクロール
        this.$nextTick(() => {
          this.scrollToSection('startTimeSection');
        });
      } else {
        // 見学の場合は既存の処理
        this.fetchAvailableTimes();
        this.$nextTick(() => {
          this.scrollToSection('durationSection');
        });
      }
    },
    onDurationChange() {
      this.fetchAvailableTimes();
      // 開始時間セクションへスクロール
      this.$nextTick(() => {
        this.scrollToSection('timeSection');
      });
    },
    selectTime(slot) {
      this.selectedTime = slot.display;
      this.emitUpdate();
      // 選択内容セクションへスクロール
      this.$nextTick(() => {
        this.scrollToSection('summarySection');
      });
    },
    scrollToSection(refName) {
      const element = this.$refs[refName];
      if (element) {
        const offset = 100; // ヘッダーの高さを考慮したオフセット
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    },
    emitUpdate() {
      if (this.reservationType === 'レンタル') {
        // レンタルの場合
        if (this.selectedStartTime && this.selectedEndTime) {
          const [startHour, startMinute] = this.selectedStartTime.split(':').map(Number);
          const [endHour, endMinute] = this.selectedEndTime.split(':').map(Number);
          const duration = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
          
          this.$emit('update:modelValue', {
            date: this.selectedDate,
            time: this.selectedStartTime,
            duration: duration,
            endTime: this.selectedEndTime
          });
        }
      } else {
        // 見学の場合
        this.$emit('update:modelValue', {
          date: this.selectedDate,
          time: this.selectedTime,
          duration: this.selectedDuration
        });
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const weekDay = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
      
      return `${year}年${month}月${day}日（${weekDay}）`;
    }
  },
  watch: {
    modelValue: {
      handler(newVal) {
        this.selectedDate = newVal.date || '';
        this.selectedTime = newVal.time || '';
        this.selectedDuration = newVal.duration || 120;
      },
      immediate: true
    },
    reservationType: {
      handler(newVal) {
        if (newVal === '見学') {
          this.selectedDuration = 60;
          this.emitUpdate();
        }
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/mixins.scss';

.reserve-calendar {
  max-width: 800px;
  margin: 0 auto;
}

.section-title {
  font-size: $font-size-lg;
  font-weight: 600;
  color: $gray-800;
  margin-bottom: $spacing-md;
}

// 日付選択
.date-selector {
  margin-bottom: $spacing-2xl;
}

.date-input {
  width: 100%;
  max-width: 300px;
  padding: $spacing-sm $spacing-md;
  font-size: $font-size-md;
  border: 1px solid $gray-300;
  border-radius: $radius-md;
  transition: all $transition-base;
  
  &:focus {
    outline: none;
    border-color: $primary-color;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
  }
}

// 利用時間選択
.duration-selector {
  margin-bottom: $spacing-2xl;
}

.duration-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: $spacing-md;
  
  @include media-breakpoint-down(sm) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.duration-option {
  display: flex;
  align-items: center;
  padding: $spacing-sm $spacing-md;
  background: $white;
  border: 1px solid $gray-300;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all $transition-base;
  
  &:hover {
    border-color: $primary-color;
    background: rgba($primary-color, 0.05);
  }
  
  input[type="radio"] {
    margin-right: $spacing-xs;
  }
  
  input[type="radio"]:checked + span {
    color: $primary-color;
    font-weight: 600;
  }
}

// 固定利用時間表示
.duration-fixed {
  padding: $spacing-md;
  background: rgba($info, 0.1);
  border-radius: $radius-md;
}

.duration-info {
  display: flex;
  align-items: center;
  margin: 0;
  color: $info;
  font-size: $font-size-md;
  
  i {
    margin-right: $spacing-sm;
  }
}

// 見学案内メッセージ
.inspection-notice {
  margin-bottom: $spacing-lg;
}

.inspection-info {
  display: flex;
  align-items: flex-start;
  padding: $spacing-md;
  background: rgba($info, 0.1);
  border-radius: $radius-md;
  color: $info;
  font-size: $font-size-sm;
  line-height: 1.6;
  margin: 0;
  
  i {
    margin-right: $spacing-sm;
    margin-top: 2px;
    flex-shrink: 0;
  }
}

// 時間選択
.time-selector {
  margin-bottom: $spacing-2xl;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $spacing-2xl;
  color: $gray-600;
  
  i {
    margin-right: $spacing-sm;
    font-size: $font-size-xl;
  }
}

.error-message {
  display: flex;
  align-items: center;
  padding: $spacing-md;
  background: rgba($danger, 0.1);
  color: $danger;
  border-radius: $radius-md;
  
  i {
    margin-right: $spacing-sm;
  }
}

.time-slots {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: $spacing-sm;
  
  @include media-breakpoint-down(sm) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.time-slot {
  padding: $spacing-sm $spacing-md;
  background: $white;
  border: 1px solid $gray-300;
  border-radius: $radius-md;
  font-size: $font-size-md;
  cursor: pointer;
  transition: all $transition-base;
  
  &:hover {
    border-color: $primary-color;
    background: rgba($primary-color, 0.05);
  }
  
  &.selected {
    background: $primary-color;
    color: $white;
    border-color: $primary-color;
  }
}

.no-slots {
  text-align: center;
  padding: $spacing-2xl;
  color: $gray-600;
  
  i {
    font-size: $font-size-3xl;
    margin-bottom: $spacing-md;
    display: block;
    color: $gray-400;
  }
  
  p {
    margin: $spacing-xs 0;
  }
}

// 開始時間選択
.start-time-selector,
.end-time-selector {
  margin-bottom: $spacing-2xl;
}

.time-select-wrapper {
  max-width: 300px;
}

.time-select {
  width: 100%;
  padding: $spacing-sm $spacing-md;
  font-size: $font-size-md;
  border: 1px solid $gray-300;
  border-radius: $radius-md;
  background: $white;
  cursor: pointer;
  transition: all $transition-base;
  
  &:focus {
    outline: none;
    border-color: $primary-color;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
  }
}

.duration-display {
  margin-top: $spacing-md;
  padding: $spacing-sm $spacing-md;
  background: rgba($primary-color, 0.05);
  border-radius: $radius-md;
  color: $primary-color;
  font-size: $font-size-md;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  
  i {
    margin-right: $spacing-sm;
  }
}

// 選択内容の確認
.selection-summary {
  background: rgba($primary-color, 0.05);
  border: 1px solid rgba($primary-color, 0.2);
  border-radius: $radius-md;
  padding: $spacing-lg;
}

.summary-content {
  display: grid;
  gap: $spacing-sm;
}

.summary-item {
  display: flex;
  align-items: center;
  
  .label {
    font-weight: 600;
    color: $gray-700;
    margin-right: $spacing-sm;
    min-width: 100px;
  }
  
  .value {
    color: $gray-800;
    
    &.price {
      font-size: $font-size-lg;
      font-weight: 600;
      color: $primary-color;
    }
  }
}
</style>